import {
  Box,
  BoxProps,
  Flex,
  Icon,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { IconType } from "react-icons";
import { HiVideoCameraSlash } from "react-icons/hi2";

import {
  CallNoteFragment,
  CallNoteType,
  CallSpeakerFragment,
} from "../../graphql";
import { noteTypeColor, noteTypeIcon } from "../CallNotes/utils";
import SliderClipIndicator from "../MediaPlayer/SliderClipIndicator";

type ProgressBarSegmentProps = {
  left: number;
  text?: string;
  icon?: IconType;
} & BoxProps & { children?: React.ReactNode };

const ProgressBarSegment: React.FC<ProgressBarSegmentProps> = ({
  left,
  text,
  icon,
  onMouseDown,
  children,
  ...rest
}) => {
  const [opacity, setOpacity] = useState(1);

  return (
    <Tooltip label={text} placement="top">
      <Box
        position="absolute"
        left={`${Math.min(0.99, left) * 100}%`}
        top="8px"
        width="16px"
        height="16px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="full"
        cursor="pointer"
        onMouseDown={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          setOpacity(0.5);
          onMouseDown?.(e);
        }}
        onMouseLeave={() => setOpacity(1)}
        onMouseUp={() => setOpacity(1)}
        opacity={opacity}
        {...rest}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export interface VideoPlayerProgressBarV2Props {
  duration: number;
  value: number;
  notes?: CallNoteFragment[];
  cameraTimestamps?: Array<{ timestamp: number; enabled: boolean }>;
  speakers?: CallSpeakerFragment[];
  seek: (value: number) => void;
  disabled: boolean;
  isClip?: boolean;
}

const VideoPlayerProgressBarV2: React.FC<VideoPlayerProgressBarV2Props> = ({
  duration,
  value,
  notes = [],
  cameraTimestamps = [],
  speakers,
  seek,
  disabled,
  isClip,
}) => {
  const sliderBackground = disabled ? "gray.100" : "whiteAlpha.400";
  const sliderFilledBackground = "white";

  const { isOpen, onOpen, onClose } = useDisclosure();

  const segments = React.useMemo(() => {
    const noteSegments = notes
      .filter(
        (note) =>
          note.type !== CallNoteType.Note && note.type !== CallNoteType.Comment
      )
      .map((note) => {
        // TODO: change colors to reflect new colors when we change it in sidebar as well
        const color = noteTypeColor(note.type);
        let placeholderText = "";
        let tooltipText = note.text || placeholderText;
        if (tooltipText.includes("\n")) {
          [tooltipText] = tooltipText.split("\n");
          tooltipText =
            tooltipText.length > 37 ? tooltipText.slice(0, 37) : tooltipText;
          tooltipText += "...";
        } else {
          // trim tooltip to 40 characters maximum
          tooltipText = tooltipText.replace(/(.{37})..+/, "$1...");
        }

        if (note.type === CallNoteType.Star) {
          placeholderText = "Noteworthy moment";
        } else if (note.type === CallNoteType.Flag) {
          placeholderText = "Potential red flag";
        }
        return (
          <ProgressBarSegment
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={() => seek(note.highlightStartTime ?? note.time)}
            key={note.id}
            text={tooltipText}
            left={note.time / duration}
          >
            <Icon
              as={noteTypeIcon(note.type)}
              color={color}
              boxSize="16px"
              style={{
                stroke: "white",
                strokeWidth: "80.5",
                strokeLinejoin: "round",
                paintOrder: "stroke",
              }}
            />
          </ProgressBarSegment>
        );
      });

    const cameraSegments = cameraTimestamps
      .filter(({ enabled }) => !enabled)
      .map(({ timestamp }) => (
        <ProgressBarSegment
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={() => seek(timestamp)}
          key={`camera-toggle-${timestamp}`}
          text="Candidate camera turned off"
          left={timestamp / duration}
        >
          <Icon as={HiVideoCameraSlash} color="orange.300" boxSize="14px" />
        </ProgressBarSegment>
      ));

    return [...noteSegments, ...cameraSegments];
  }, [notes, cameraTimestamps, duration, speakers, seek]);
  return (
    <Box height="12" display="flex" alignItems="center" width="100%">
      <Flex lineHeight={1} height={8} width="100%">
        {isClip && <SliderClipIndicator isStart />}
        <Slider
          value={value}
          onChange={(value) => seek(value)}
          max={duration || undefined}
          isDisabled={disabled}
          cursor={disabled ? "not-allowed" : "pointer"}
          focusThumbOnChange={false}
        >
          <SliderTrack bg={sliderBackground} h="2">
            {!disabled && (
              <SliderFilledTrack bg={sliderFilledBackground} h="2" />
            )}
          </SliderTrack>
          <SliderThumb
            w={isOpen ? "7" : "4"}
            h={isOpen ? "7" : "4"}
            opacity={1}
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.3)"
            zIndex="auto"
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            onFocus={(e) => e.preventDefault()}
            onFocusCapture={(e) => e.preventDefault()}
            data-testid="call-slider-thumb"
          />
          {!disabled && duration && segments}
        </Slider>
        {isClip && <SliderClipIndicator isStart={false} />}
      </Flex>
    </Box>
  );
};

export default VideoPlayerProgressBarV2;
